import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, LogOut, KeyRound, Check, Loader2, ShoppingCart, Package, ShieldCheck, Boxes, BriefcaseBusiness, Coffee, Truck, Warehouse } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useBranchAuth, API } from '../context/BranchAuthContext';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from './ImageUpload';
import { useToast } from '../hooks/use-toast';
import BranchAttendanceDialog from './BranchAttendanceDialog';

/**
 * BranchShell — shared chrome for every page inside the /branch portal.
 * Provides:
 *   - sticky brand header with logo + branch name
 *   - inline nav ("New Order" | "My Orders")
 *   - Password + Sign-out actions
 *   - forced-password-change banner + inline form
 * Content pages render inside <main>.
 */
const NAV = [
  { to: '/branch',         label: 'New Order', icon: ShoppingCart, testid: 'branch-nav-new-order' },
  { to: '/branch/orders',  label: 'My Orders', icon: Package,      testid: 'branch-nav-my-orders' },
  { to: '/branch/pallets', label: 'Pallets',   icon: Boxes,        testid: 'branch-nav-pallets' },
  // Feb 2026 — Branch FEFO WMS · Phase 2/3. Only surfaces when the
  // branch has a warehouse binding AND the super admin has ticked
  // "Enable Receiving" on the branch account. Server enforces scope
  // either way; the flag just controls visibility.
  { to: '/branch/receiving',  label: 'Receiving',  icon: Truck,     testid: 'branch-nav-receiving',  requiresReceiving: true },
  { to: '/branch/live-stock', label: 'Live Stock', icon: Warehouse, testid: 'branch-nav-live-stock', requiresReceiving: true },
  { to: '/branch/my-work', label: 'My Work',   icon: BriefcaseBusiness, testid: 'branch-nav-my-work' },
  { to: '/branch/desk-tracker', label: 'Desk',  icon: Coffee, testid: 'branch-nav-desk-tracker' },
];

const BranchShell = ({ children }) => {
  const { branch, logout, authHeaders, isSuperBypass } = useBranchAuth();
  const { settings } = useSettings();
  const logoUrl = resolveImageUrl(settings?.logo_image || '');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toast } = useToast();
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '' });
  const [pwdBusy, setPwdBusy] = useState(false);

  // Feb 2026 — Attendance heartbeat. Pings `/branch/me` on mount so
  // the Attendance Log picks up `last_activity_at` for worked-hours
  // math. Best-effort; never blocks the UI.
  useEffect(() => {
    if (!authHeaders?.Authorization) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/branch/me`, { headers: authHeaders });
      } catch { /* soft-fail */ }
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const changePassword = async (e) => {
    e.preventDefault();
    if (!pwd.current || !pwd.next) return;
    if (pwd.next.length < 8) { toast({ title: 'New password must be at least 8 characters' }); return; }
    setPwdBusy(true);
    try {
      await axios.post(`${API}/branch/change-password`, { current_password: pwd.current, new_password: pwd.next }, { headers: authHeaders });
      toast({ title: 'Password updated' });
      setPwd({ current: '', next: '' });
      setShowChangePwd(false);
    } catch (err) {
      toast({ title: 'Change failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setPwdBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="Mohammed Riaz & Partner LLC" className="h-10 w-auto object-contain" data-testid="branch-header-logo" />
          ) : (
            <Building2 className="w-8 h-8 text-[#0078D4]" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold tracking-[0.2em] text-[#0078D4] uppercase">Branch Portal</div>
            <div className="text-sm font-semibold text-[#0F172A] truncate" data-testid="branch-header-name">
              {branch?.name || branch?.username || '—'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowChangePwd((v) => !v)}
            disabled={isSuperBypass}
            title={isSuperBypass ? 'Manage password from the admin portal' : ''}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${
              isSuperBypass
                ? 'text-neutral-300 cursor-not-allowed'
                : 'text-neutral-600 hover:text-[#0F172A] hover:bg-neutral-100'
            }`}
            data-testid="branch-change-password-toggle"
          >
            <KeyRound className="w-3.5 h-3.5" /> Password
          </button>
          <button
            type="button"
            onClick={() => {
              if (isSuperBypass) {
                // Super admin never had a branch session — just leave the portal.
                navigate('/admin', { replace: true });
              } else {
                logout();
                navigate('/branch/login', { replace: true });
              }
            }}
            className="text-xs font-semibold text-neutral-600 hover:text-red-600 px-3 py-1.5 rounded-full hover:bg-red-50 inline-flex items-center gap-1"
            data-testid="branch-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> {isSuperBypass ? 'Back to admin' : 'Sign out'}
          </button>
        </div>
        {/* Nav strip */}
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 -mb-px" data-testid="branch-nav">
          {NAV
            .filter((item) => {
              // Feb 2026 — Hide receiving/live-stock tabs unless the
              // branch is bound to a warehouse AND has been granted
              // receiving. Super admins bypass the branch shell
              // entirely so they don't need the guard here.
              if (!item.requiresReceiving) return true;
              const b = branch || {};
              return Boolean(b.warehouse_id) && Boolean(b.has_receiving);
            })
            .map(({ to, label, icon: Icon, testid }) => {
            const active = pathname === to || (to !== '/branch' && pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 transition ${
                  active
                    ? 'border-[#0078D4] text-[#0078D4]'
                    : 'border-transparent text-neutral-500 hover:text-[#0F172A]'
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </Link>
            );
          })}
        </nav>
      </header>

      {isSuperBypass && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
          <div className="rounded-xl bg-indigo-50 border border-indigo-200 px-4 py-3 text-sm text-indigo-900 flex items-center gap-2" data-testid="branch-super-admin-banner">
            <ShieldCheck className="w-4 h-4 text-indigo-700 shrink-0" />
            <span>
              You&apos;re viewing the branch portal as <strong>Super Admin</strong> — every branch&apos;s orders are visible. Placing new orders is disabled in this mode.
            </span>
          </div>
        </div>
      )}

      {!isSuperBypass && branch?.must_change_password && !showChangePwd && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 flex items-center gap-2" data-testid="branch-must-change-password-banner">
            <KeyRound className="w-4 h-4" />
            <span>Please change your temporary password.</span>
            <button
              type="button"
              onClick={() => setShowChangePwd(true)}
              className="ml-auto text-xs font-bold text-amber-900 underline"
            >
              Change now
            </button>
          </div>
        </div>
      )}

      {showChangePwd && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
          <form onSubmit={changePassword} className="rounded-2xl bg-white border border-neutral-200 p-4" data-testid="branch-change-password-form">
            <div className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Change password</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input type="password" required value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} placeholder="Current password" className="text-sm" data-testid="branch-current-password" />
              <Input type="password" required value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} placeholder="New password (min 8 chars)" className="text-sm" data-testid="branch-new-password" />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Button type="submit" disabled={pwdBusy} className="rounded-full bg-[#0F172A] text-white h-9" data-testid="branch-save-password">
                {pwdBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Save</>}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowChangePwd(false)} className="rounded-full h-9">Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">{children}</main>
      <BranchAttendanceDialog />
    </div>
  );
};

export default BranchShell;
